import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Star, Camera, Upload, X } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { imgUrl } from "@/utils/format";
import { toast } from "sonner";

export default function ReviewForm({ orderId, onSubmitted }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (photos.length + files.length > 4) {
      toast.error(t("rvf.max4"));
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await apiClient.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => [...prev, data.url]);
      }
      toast.success(t("rvf.photosAdded", { n: files.length }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (url) => setPhotos((prev) => prev.filter((p) => p !== url));

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error(t("rvf.oneStar"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/reviews", { order_id: orderId, rating, comment, photos });
      toast.success(t("rvf.thanks"));
      onSubmitted?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-sand rounded-2xl p-6">
      <h3 className="font-display text-xl font-semibold mb-2">{t("rvf.title")}</h3>
      <p className="text-sm text-mute mb-4">{t("rvf.sub")}</p>
      <div className="flex gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            type="button"
            key={n}
            data-testid={`review-star-${n}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setRating(n)}
            className="transition-transform hover:scale-110"
          >
            <Star className={`w-9 h-9 ${(hover || rating) >= n ? "fill-[#F4C430] text-[#F4C430]" : "text-mute"}`} />
          </button>
        ))}
      </div>
      <textarea
        data-testid="review-comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={t("rvf.ph")}
        rows={3}
        maxLength={500}
        className="w-full bg-elevated rounded-xl px-4 py-3 outline-none"
      />

      {photos.length > 0 && (
        <div className="flex gap-2 flex-wrap mt-3">
          {photos.map((p, i) => (
            <div key={i} className="relative">
              <img src={imgUrl(p)} alt={`photo ${i + 1}`} className="w-24 h-24 rounded-xl object-cover" />
              <button type="button" onClick={() => removePhoto(p)} data-testid={`review-photo-remove-${i}`} aria-label={t("rvf.removePhoto")} className="absolute -top-1.5 -right-1.5 bg-ink text-white rounded-full p-1">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap mt-3">
        <label data-testid="review-photo-camera" className={`inline-flex items-center gap-1.5 text-xs rounded-full bg-brand-red text-white px-3 py-2 cursor-pointer ${uploading || photos.length >= 4 ? "opacity-60 pointer-events-none" : ""}`}>
          <Camera className="w-3 h-3" /> {uploading ? t("rvf.sending") : t("rvf.dishPhoto")}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleUpload} disabled={photos.length >= 4} />
        </label>
        <label data-testid="review-photo-upload" className={`inline-flex items-center gap-1.5 text-xs rounded-full border border-sand px-3 py-2 cursor-pointer hover:bg-elevated ${uploading || photos.length >= 4 ? "opacity-60 pointer-events-none" : ""}`}>
          <Upload className="w-3 h-3" /> {t("rvf.file")} ({photos.length}/4)
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={handleUpload} disabled={photos.length >= 4} />
        </label>
      </div>

      <button type="submit" disabled={loading || uploading} data-testid="submit-review" className="mt-4 w-full rounded-full bg-brand-red hover:bg-[#C81F2D] text-white py-3 font-semibold disabled:opacity-60">
        {loading ? t("rvf.submitting") : t("rvf.submit")}
      </button>
    </form>
  );
}
